import { applyListingFailure, describeThrownFailure, type ListingFailureHandlers } from './listingFailure';

/**
 * One offering-listing request, from dispatch to the last write it is allowed to make
 * (task-djasurv8m7).
 *
 * ⛔⛔ WHY THIS IS A FUNCTION AND NOT TWENTY LINES INSIDE THE COMPONENT. Every blocker on this task
 * has been about the ORDER of two things — an await and a guard — and neither an assertion about
 * `commitIfCurrent` alone nor one about `createRequestGate` alone can see that order. Inline in a
 * ~4,900-line component the only available check was "the source mentions commitIfCurrent after
 * response.json()", which is a grep, not a test: it cannot distinguish a guard that is called at
 * the right place from one that decided at the wrong time.
 * 🔑 A DEFECT THAT LIVES IN THE COMPOSITION CANNOT BE FOUND BY TESTING THE PARTS. So the
 *   composition is the unit: a test can now start two of these against one gate, hold A's body
 *   parse open, let B finish, and watch what A does when it wakes.
 *
 * ⛔ EVERY await IS A PLACE THE WORLD CHANGES. There are two on the failure path (the response, then
 *   the error body) and two on the success path, and `commit` is consulted after the LAST one on
 *   each — never earlier, and never cached.
 */
export interface ListingResponse {
  ok: boolean;
  status: number;
  json: () => Promise<any>;
}

export interface ListingRequestDeps {
  /** Issues the request. Called once, synchronously, by the caller's dispatch. */
  fetchListing: () => Promise<ListingResponse>;
  /**
   * Applies a write only if this request is still the one permitted to paint. MUST evaluate its
   * predicates when called — see `createCommitGuard`.
   */
  commit: (write: () => void) => void;
  /** Paints a successful body. */
  onSuccess: (data: any) => void;
  /** The handlers the shared failure path writes through. */
  failureHandlers: ListingFailureHandlers;
  /** Clears the spinner. Belongs to the latest request like every other write. */
  onSettled: () => void;
  /** Injected so a test can assert we still report the cause; defaults to the console. */
  logError?: (message: string, detail: unknown) => void;
}

export async function runListingRequest(deps: ListingRequestDeps): Promise<void> {
  const { fetchListing, commit, onSuccess, failureHandlers, onSettled } = deps;
  const logError = deps.logError ?? ((m: string, d: unknown) => console.error(m, d));

  try {
    const response = await fetchListing();
    if (response.ok) {
      const data = await response.json();
      // ⇒ The guard is consulted HERE, after the parse, not before the fetch.
      commit(() => onSuccess(data));
    } else {
      /**
       * ⛔⛔ A FAILED LISTING REQUEST USED TO CHANGE NOTHING AT ALL. There was no `else`: a non-ok
       * response skipped every setter and the `catch` never fires for an HTTP error, so the PREVIOUS
       * page stayed on screen under the operator's new search. On p4 that read "Showing 1-50 of
       * 8201" for a search that had actually 500'd — "everything matched your term" — and Select All
       * then targeted 2,058 products the operator never filtered to. THE WIDENING WAS IN THE
       * SILENCE, not in the query.
       */
      let detail = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        if (body?.error) detail = `${detail} — ${body.error}`;
      } catch {
        // Body was not JSON; the status alone is the honest detail.
      }
      logError('[Offering] listing request failed:', detail);
      commit(() => applyListingFailure(detail, failureHandlers));
    }
  } catch (error) {
    // ⛔⛔ THE CATCH IS A SILENCE TOO, and leaving it was the same defect one branch over: a rejected
    // fetch, a timeout, or a body that will not decode all land here, and this branch used to toast
    // and return, leaving the previous page beneath the operator's new search term.
    // ⇒ Same single handler as the HTTP-error branch, so a third failure mode cannot diverge.
    logError('Error loading offering data:', error);
    commit(() => applyListingFailure(describeThrownFailure(error), failureHandlers));
  } finally {
    // ⚠️ The spinner belongs to the LATEST request too: a superseded response clearing it would
    // report "done" while the request the operator is actually waiting on is still in flight.
    commit(() => onSettled());
  }
}
